import { calendarDay, pointTime } from '../../shared/lib/format';
import { SectionPanel } from '../../shared/ui/section-panel';
import {
  formatBytes,
  ownerName,
  periodLabel,
  periodOfDays,
  quotaText,
  type TRetentionUsage,
  type TStorageCleanup,
} from '../../entities/retention';

const SHOWN = 5;

interface IQuotaCleanupCardProps {
  run: TStorageCleanup;
  usage: TRetentionUsage;
  now: number;
  timeZone?: string;
}

// Over the quota one cutoff applies to the whole organization, Forever included (board S3).
export const QuotaCleanupCard = ({ run, usage, now, timeZone }: IQuotaCleanupCardProps) => {
  const flows = run.owners.filter((owner) => owner.kind === 'flow');
  const older = run.owners.filter((owner) => owner.kind === 'older');
  const shown = [...flows.slice(0, SHOWN), ...older];
  const cutoff = run.historyBefore ?? run.contentBefore;
  const tookHistory = run.historyBefore !== null;

  return (
    <SectionPanel
      title="Removed to get under the quota"
      note={`${pointTime(run.startedAt, now, timeZone)}${run.finishedAt ? '' : ' · still removing'}`}
    >
      <div className="px-card py-4">
        <p className="text-meta leading-18 text-primary">
          {run.usedBytes !== null ? `You were at ${formatBytes(run.usedBytes)}. ` : ''}
          Cleanup removed the oldest{' '}
          {tookHistory ? 'content and history' : 'webhook bodies, responses and step results'} until
          you were back under {quotaText(usage.quotaBytes)}.{' '}
          {tookHistory
            ? 'Those runs are gone from Events too.'
            : 'History stays: those runs still show in Events, without their content.'}
        </p>
        <div className="mt-3 flex flex-col">
          {shown.map((owner) => (
            <div
              key={`${owner.kind}:${owner.id}`}
              className="flex justify-between gap-3 border-t border-border-row py-2 text-meta"
            >
              <span className="min-w-0 text-primary">
                {ownerName(owner)}
                {owner.contentDays ? (
                  <span className="text-muted">
                    {' '}
                    · keeps {periodLabel(periodOfDays(owner.contentDays))}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-right text-primary">
                {cutoff
                  ? `${tookHistory ? 'before' : 'content before'} ${calendarDay(cutoff, now, timeZone)} · `
                  : ''}
                {formatBytes(owner.bytes)}
              </span>
            </div>
          ))}
          <div className="flex justify-between gap-3 border-t border-border-row py-2 text-meta">
            <span className="text-muted">
              {flows.length.toLocaleString('en-US')} {flows.length === 1 ? 'flow' : 'flows'} ·{' '}
              {run.removedRows.toLocaleString('en-US')} records
            </span>
            <span className="font-mono text-primary">{formatBytes(run.removedBytes)}</span>
          </div>
        </div>
        <p className="mt-3 font-mono text-micro text-faint">
          Still being delivered and bookmarked: never removed, quota or not.
        </p>
      </div>
    </SectionPanel>
  );
};
